import { useAuth } from '@platform/auth-client';
import { guardedStorage } from '@platform/design-system';
import { useEffect, useState } from 'react';
import { FINANCE_READ_ROLE } from '../finance/finance.ts';
import { DashboardHeader } from './DashboardHeader.tsx';
import { useWidgetContext, type DashboardProps } from './dashboard-props.ts';
import { DashboardDialogs, type Naming } from './widgets/DashboardDialogs.tsx';
import { DashboardGrid } from './widgets/DashboardGrid.tsx';
import { defaultViewOf, useDashboardViewChanges, useDashboardViews } from './widgets/dashboard-views.ts';
import { LEGACY_TILES_KEY, standardLayoutOf } from './widgets/default-layout.ts';
import { EditBar } from './widgets/EditBar.tsx';
import { useDashboardEditor } from './widgets/use-dashboard-editor.ts';
import { ViewControls } from './widgets/ViewControls.tsx';
import type { DashboardView } from './widgets/widget-types.ts';
import { WidgetGallery } from './widgets/WidgetGallery.tsx';
import { WidgetSettingsDialog } from './widgets/WidgetSettingsDialog.tsx';

const activeViewOf = (views: readonly DashboardView[], viewId: string | null): DashboardView | undefined =>
  viewId === null ? defaultViewOf(views) : views.find((view) => view.id === viewId);

export const PersonalizedDashboard = (props: DashboardProps) => {
  const { api, viewId, onViewChange, onDirtyChange } = props;
  const financeVisible = useAuth().hasRole(FINANCE_READ_ROLE);
  const [standard] = useState(() =>
    standardLayoutOf(guardedStorage.getItem(LEGACY_TILES_KEY) as string | null),
  );
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [naming, setNaming] = useState<Naming | null>(null);
  const [deleting, setDeleting] = useState<DashboardView | null>(null);
  const views = useDashboardViews(api).data?.items ?? [];
  const changes = useDashboardViewChanges(api);
  const active = activeViewOf(views, viewId);
  const shown = active?.layout ?? standard;
  const editor = useDashboardEditor(shown);
  useEffect(() => {
    onDirtyChange?.(editor.dirty);
  }, [editor.dirty, onDirtyChange]);
  const context = useWidgetContext(props);
  const layout = editor.draft ?? shown;

  const saveAs = (name: string) => {
    changes.mutate(
      { kind: 'create', name, layout },
      {
        onSuccess: (view) => {
          setNaming(null);
          editor.cancel();
          onViewChange(view?.id ?? null);
        },
      },
    );
  };
  const save = () => {
    if (active === undefined) {
      setNaming({ mode: 'saveAs' });
      return;
    }
    changes.mutate({ kind: 'update', view: active, name: active.name, layout }, { onSuccess: editor.cancel });
  };

  return (
    <>
      <DashboardHeader
        {...props}
        range={context.range}
        controls={
          <ViewControls
            views={views}
            active={active}
            editing={editor.draft !== null}
            onSelect={onViewChange}
            onCustomize={editor.start}
            onMakeDefault={(view) => {
              changes.mutate({ kind: 'default', view });
            }}
            onRename={(view) => {
              setNaming({ mode: 'rename', view });
            }}
            onDelete={setDeleting}
          />
        }
      />
      {editor.draft === null ? null : (
        <EditBar
          full={editor.full}
          saving={changes.isPending}
          onAdd={() => {
            setGalleryOpen(true);
          }}
          onCancel={editor.cancel}
          onSaveAs={() => {
            setNaming({ mode: 'saveAs' });
          }}
          onSave={save}
        />
      )}
      <DashboardGrid
        layout={layout}
        context={context}
        financeVisible={financeVisible}
        editor={editor.draft === null ? undefined : editor.grid}
      />
      <WidgetGallery
        open={galleryOpen}
        full={editor.full}
        financeVisible={financeVisible}
        onClose={() => {
          setGalleryOpen(false);
        }}
        onAdd={(preset) => {
          editor.add(preset);
          setGalleryOpen(false);
        }}
      />
      {editor.configuring === null ? null : (
        <WidgetSettingsDialog
          widget={editor.configuring}
          context={context}
          onClose={editor.closeSettings}
          onSave={editor.apply}
        />
      )}
      <DashboardDialogs
        naming={naming}
        deleting={deleting}
        onName={(chosen, name) => {
          if (chosen.mode === 'saveAs') {
            saveAs(name);
            return;
          }
          changes.mutate(
            { kind: 'update', view: chosen.view, name, layout: chosen.view.layout },
            {
              onSuccess: () => {
                setNaming(null);
              },
            },
          );
        }}
        onCloseNaming={() => {
          setNaming(null);
        }}
        onConfirmDelete={(view) => {
          changes.mutate(
            { kind: 'delete', view },
            {
              onSuccess: () => {
                setDeleting(null);
                onViewChange(null);
              },
            },
          );
        }}
        onCloseDelete={() => {
          setDeleting(null);
        }}
      />
    </>
  );
};
